<script setup lang="ts">
import { translate } from '@/plugins/i18n.js'
import {
  readRedirectOrigin,
  SUCCESSOR_REDIRECT_OPT_OUT_PARAM,
  trackSuccessorRedirect,
  type SuccessorRedirectOrigin
} from '@/services/helpers/SuccessorRedirectHelper'
import { usePdpStore } from '@/stores/pdp'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'

const SvgWrapper = defineAsyncComponent(
  () => import('@/components/basic/atoms/SvgWrapper.vue')
)

/**
 * Tells a visitor the sold-out vintage they came for sent them here, and
 * leads back to it (VIP-12594). What it says lives in the history entry the
 * redirect created, not in the URL: a shared or bookmarked successor link
 * shows no notice, and the URL adds no variant to the page cache. Read on
 * mount only — the server render has no history, so rendering it there
 * would break hydration.
 */
const { getApiProduct: product, isUpdating: updating } =
  storeToRefs(usePdpStore())

const origin = ref<SuccessorRedirectOrigin | null>(null)

// Named years: visitors recognise the vintage they clicked on, and learn
// which one they are looking at now
const hasVintages = computed(
  () => !!origin.value?.wine_year && !!origin.value?.successor_wine_year
)
const text = computed(() =>
  hasVintages.value
    ? translate(
        'pdp_successor_notice',
        origin.value!.wine_year,
        origin.value!.successor_wine_year
      )
    : translate('pdp_successor_notice_generic')
)
const backText = computed(() =>
  origin.value?.wine_year
    ? translate('pdp_successor_notice_back', origin.value.wine_year)
    : translate('pdp_successor_notice_back_generic')
)
const backLink = computed(() => ({
  path: origin.value?.path ?? '/',
  query: { [SUCCESSOR_REDIRECT_OPT_OUT_PARAM]: '1' }
}))

const _trackingData = (from: SuccessorRedirectOrigin) => ({
  product_id: from.product_id,
  campaign_id: from.campaign_id,
  wine_year: from.wine_year,
  successor_product_id: from.successor_product_id,
  successor_wine_year: from.successor_wine_year
})

// One view per redirect, however often the product reloads in between (a
// query change reloads it); by ids, since history.state may hand out a fresh
// copy per read
const viewed = new Set<string>()

const refresh = () => {
  origin.value = readRedirectOrigin(product.value.product_id)
  if (!origin.value) return
  const key = `${origin.value.product_id}>${origin.value.successor_product_id}`
  if (viewed.has(key)) return
  viewed.add(key)
  void trackSuccessorRedirect('view', _trackingData(origin.value))
}

const onBack = () => {
  if (origin.value) {
    void trackSuccessorRedirect('back', _trackingData(origin.value))
  }
}

// The redirect lands while the successor still loads, and every navigation
// reloads the product: following the product on screen follows the entry too.
// Watched from mount on, so the server render never reads history.
onMounted(() => {
  watch(() => product.value.product_id, refresh, { immediate: true })
})
</script>

<!-- The stage's banderole, like TheBanners: the one place on the PDP visitors
     already read as "this is about this wine". Beige rather than the page's
     light beige, so it stands out against the stage and the page alike -->
<template>
  <output
    v-if="origin && !updating"
    class="successor-notice w-100 bg-beige c-graphit d-flex align-center justify-center p-2"
    data-testid="successor-redirect-notice"
  >
    <!-- <output> carries the status role (a polite live region) natively -->
    <SvgWrapper
      use="icon_info_borderless"
      dimensions="25"
      class="successor-notice-icon bg-white f-winered rounded-circle p-1 flex-shrink-0 mr-2"
      aria-hidden="true"
    />
    <div
      class="successor-notice-body d-flex flex-wrap align-center justify-center text-center font-size-regular"
    >
      <span class="successor-notice-text font-weight-bold">{{ text }}</span>
      <RouterLink
        :to="backLink"
        class="successor-notice-back c-winered"
        data-testid="successor-redirect-back"
        @click="onBack"
      >
        {{ backText }}
      </RouterLink>
    </div>
  </output>
</template>

<style scoped>
.successor-notice-body {
  column-gap: 0.75rem;
  row-gap: 0.125rem;
}

.successor-notice-text {
  text-wrap: balance;
}

.successor-notice-back {
  padding-block: 0.25rem;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  white-space: nowrap;
}

.successor-notice-back:hover {
  text-decoration-thickness: 2px;
}

/* The browser's default ring is hard to see on the beige background */
.successor-notice-back:focus-visible {
  outline: 2px solid rgb(var(--v-theme-winered));
  outline-offset: 2px;
  border-radius: 2px;
}

/* Narrow screens: the text gets the full width, the way back its own line
   (TheBanners drops the banderole icon the same way) */
@media (max-width: 599.98px) {
  .successor-notice-icon {
    display: none;
  }

  .successor-notice-back {
    flex-basis: 100%;
  }
}
</style>
